import './FvtInvite.scss';

import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { Alert, Col, Container, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import { faPaperPlane } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@myome/ui';

import { FormPanel } from '@components/FormPanel/FormPanel';
import { BridgeBioMaterialCode } from '@components/marketing/BridgeBioMaterialCode/BridgeBioMaterialCode';
import { PageIntro } from '@components/PageIntro/PageIntro';
import { ROUTES } from '@constants/routes';
import { recordGoogleAnalyticsEvent } from '@utils/helpers';

import { useReferralContext } from '../referrals/ReferralRoot';
import { InviteEducationSidebar } from './components/InviteEducationSidebar';
import { InviteFormFields } from './components/InviteFormFields';
import {
  FVTFamilyMemberFormValues,
  getDependentFieldErrors,
  getInitialInviteValues,
  inviteRelativeSchema,
  toFamilyMemberCreateRequest,
} from './FvtInvite.validation';

export const FvtInvite = () => {
  const { referral, createFamilyMember } = useReferralContext();
  const familyMembers = referral.family_members;

  const navigate = useNavigate();
  const [showValidationBanner, setShowValidationBanner] = useState(false);

  const handleSubmit = async (values: FVTFamilyMemberFormValues, helpers: FormikHelpers<FVTFamilyMemberFormValues>) => {
    setShowValidationBanner(false);

    const dependentFieldErrors = getDependentFieldErrors(values, familyMembers);

    let hasDependentFieldErrors = false;
    Object.entries(dependentFieldErrors).forEach(([field, error]) => {
      if (error) {
        hasDependentFieldErrors = true;
        helpers.setFieldError(field, error);
      }
    });

    if (hasDependentFieldErrors) {
      setShowValidationBanner(true);
      return;
    }

    recordGoogleAnalyticsEvent('User Interaction', 'Form Submit', 'BridgeBio FVT Proband Invite');

    await createFamilyMember(toFamilyMemberCreateRequest(values));
    navigate(ROUTES.FVT_FAMILY);
  };

  const formik = useFormik({
    initialValues: getInitialInviteValues(),
    validationSchema: inviteRelativeSchema,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    if (formik.isValid) {
      setShowValidationBanner(false);
    }
  }, [formik.isValid]);

  return (
    <div className='fvt-invite-page pt-4 w-100'>
      <Container fluid='xxl' as='main' className='fvt-invite-page__main pb-5'>
        <PageIntro
          className='mb-4'
          breadcrumbs={[
            { label: 'Family Cascade Genetic Testing', to: ROUTES.FVT_FAMILY },
            { label: 'Invite a relative' },
          ]}
          title='Invite an eligible relative'
          description="Enter their details below. We'll email this person a private invitation to opt in."
        />

        <Row className='g-4 g-lg-5 align-items-start'>
          <Col lg={8}>
            {showValidationBanner && (
              <Alert variant='danger' className='mb-4'>
                <Alert.Heading as='h2'>Please fix the highlighted fields</Alert.Heading>
                <p className='mb-0'>
                  Check required fields and this relative&apos;s date of birth before sending the invitation.
                </p>
              </Alert>
            )}

            <FormikProvider value={formik}>
              <Form className='fvt-invite-form'>
                <FormPanel maxWidth='full' layout='sectioned'>
                  <FormPanel.SectionHeader title="Relative's details" subtitle='A blood relative up to 4th degree' />

                  <InviteFormFields />

                  <FormPanel.Footer
                    className='fvt-invite-form__footer'
                    preActions={
                      <p className='fvt-invite-form__legal mb-0'>
                        By submitting, you confirm this person is your blood relative (up to 4th degree) and that you
                        understand they&apos;ll be contacted with a private invitation. They are not shown your report
                        or your name unless you tell them.
                      </p>
                    }
                  >
                    <Button variant='secondary' to={ROUTES.FVT_FAMILY}>
                      Cancel
                    </Button>

                    <Button
                      variant='primary'
                      type='submit'
                      endIcon={<FontAwesomeIcon icon={faPaperPlane} />}
                      disabled={formik.isSubmitting}
                    >
                      {formik.isSubmitting ? 'Sending…' : 'Send invitation'}
                    </Button>
                  </FormPanel.Footer>
                </FormPanel>
              </Form>
            </FormikProvider>
          </Col>

          <Col lg={4}>
            <InviteEducationSidebar />
          </Col>
        </Row>

        <div className='pt-4'>
          <BridgeBioMaterialCode />
        </div>
      </Container>
    </div>
  );
};
